/**
 * NoCodeBackend (NCB) Service
 * Refactored to use path-based routing as required by the Cloudflare proxy.
 */

const PROXY_BASE = '/api/ncb';

/**
 * Gets the current timestamp in MySQL-compatible YYYY-MM-DD HH:mm:ss format
 * forced to Asia/Manila timezone.
 */
const getManilaTimestamp = () => {
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(date);
    const getPart = (type) => parts.find(p => p.type === type).value;
    
    return `${getPart('year')}-${getPart('month')}-${getPart('day')} ${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
  } catch (e) {
    const pad = (n) => n.toString().padStart(2, '0');
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
};

/**
 * Generic fetch wrapper for NCB via Proxy
 */
const fetchProxy = async (method, operation, table, data = null, id = null, params = null) => {
  let path = `${operation}/${table}`;
  if (id) path += `/${id}`;
  
  let queryString = '';
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null) {
        searchParams.append(key, val);
      }
    });
    queryString = `?${searchParams.toString()}`;
  }
  
  const url = `${PROXY_BASE}/${path}${queryString}`;
  
  const options = {
    method: method,
    headers: { 'Content-Type': 'application/json' }
  };

  if (data && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, options);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.message || `NCB ${operation} failed: ${response.status}`);
    }

    return result;
  } catch (err) {
    console.error(`[NCB Service] Error:`, err);
    throw err;
  }
};

export const ncbService = {
  /**
   * Fetches all records by iterating through all available pages.
   * Ensures no records are silently capped.
   */
  getAllRecords: async (table, customLimit = 100) => {
    let allData = [];
    let currentPage = 1;
    let hasMore = true;
    const pageSize = customLimit;
    const maxPages = 50;

    console.log(`[NCB Service] Starting exhaustive fetch for ${table}...`);

    try {
      while (hasMore && currentPage <= maxPages) {
        const response = await fetchProxy('GET', 'read', table, null, null, {
          page: currentPage,
          limit: pageSize
        });

        const data = Array.isArray(response) ? response : (response.data || []);
        allData = [...allData, ...data];

        const metadata = response.metadata || {};
        
        if (metadata.hasMore !== undefined) {
          hasMore = metadata.hasMore === true;
        } else if (metadata.totalPages !== undefined) {
          hasMore = currentPage < metadata.totalPages;
        } else if (metadata.total_pages !== undefined) {
          hasMore = currentPage < metadata.total_pages;
        } else {
          hasMore = data.length >= pageSize;
        }

        console.log(`[NCB Service] ${table} Page ${currentPage}: ${data.length} records. hasMore: ${hasMore}`);
        
        if (hasMore) {
          currentPage++;
        }
      }

      console.log(`[NCB Service] Exhaustive fetch complete for ${table}. Total: ${allData.length}`);
      return allData;
    } catch (err) {
      console.error(`[NCB Service] Failed to fetch all ${table}:`, err);
      throw err;
    }
  },

  getBookings: async () => {
    return ncbService.getAllRecords('bookings');
  },
  
  createBooking: async (data) => {
    const cleanedPayload = {
      booking_title: data.booking_title,
      guest_name: data.guest_name,
      guest_contact: `${data.guest_email || ''} | ${data.guest_phone || ''}`,
      start_date: data.start_date,
      end_date: data.end_date,
      booking_status: data.booking_status || 'pending',
      notes: data.notes || '',
      admin_notes: '',
      source: data.source || 'website',
      total_guests: data.total_guests,
      total_amount: data.total_amount
    };
    const result = await fetchProxy('POST', 'create', 'bookings', cleanedPayload);
    return result.data || result;
  },

  updateBooking: async (id, data) => {
    const result = await fetchProxy('PUT', 'update', 'bookings', data, id);
    return result.data || result;
  },

  deleteBooking: async (id) => {
    const result = await fetchProxy('DELETE', 'delete', 'bookings', null, id);
    return result.data || result;
  },

  getContactMessages: async () => {
    return ncbService.getAllRecords('contact_messages');
  },

  createContactMessage: async (data) => {
    const now = getManilaTimestamp();
    const payload = {
      name: data.name,
      email: data.email,
      phone: data.phone || '',
      subject: data.subject || '',
      message: data.message,
      status: 'new',
      admin_notes: '',
      created_at: now,
      updated_at: now
    };
    console.log("[NCB Service] Creating contact message with payload:", payload);
    const result = await fetchProxy('POST', 'create', 'contact_messages', payload);
    return result.data || result;
  },

  updateContactMessage: async (id, data) => {
    const result = await fetchProxy('PUT', 'update', 'contact_messages', {
      ...data,
      updated_at: getManilaTimestamp()
    }, id);
    return result.data || result;
  },

  getGalleryImages: async () => {
    return ncbService.getAllRecords('gallery_images');
  },
  createGalleryImage: async (data) => {
    const result = await fetchProxy('POST', 'create', 'gallery_images', data);
    return result.data || result;
  },
  updateGalleryImage: async (id, data) => {
    const result = await fetchProxy('PUT', 'update', 'gallery_images', data, id);
    return result.data || result;
  },
  deleteGalleryImage: async (id) => {
    const result = await fetchProxy('DELETE', 'delete', 'gallery_images', null, id);
    return result.data || result;
  },

  getGalleryVideos: async () => {
    return ncbService.getAllRecords('gallery_videos');
  },
  createGalleryVideo: async (data) => {
    const result = await fetchProxy('POST', 'create', 'gallery_videos', data);
    return result.data || result;
  },
  updateGalleryVideo: async (id, data) => {
    const result = await fetchProxy('PUT', 'update', 'gallery_videos', data, id);
    return result.data || result;
  },
  deleteGalleryVideo: async (id) => {
    const result = await fetchProxy('DELETE', 'delete', 'gallery_videos', null, id);
    return result.data || result;
  },

  getTestimonials: async () => {
    return ncbService.getAllRecords('testimonials');
  },
  createTestimonial: async (data) => {
    const result = await fetchProxy('POST', 'create', 'testimonials', data);
    return result.data || result;
  },
  updateTestimonial: async (id, data) => {
    const result = await fetchProxy('PUT', 'update', 'testimonials', data, id);
    return result.data || result;
  },
  deleteTestimonial: async (id) => {
    const result = await fetchProxy('DELETE', 'delete', 'testimonials', null, id);
    return result.data || result;
  },

  getSiteSettings: async () => {
    return ncbService.getAllRecords('site_settings_7523');
  },
  updateSiteSetting: async (id, data) => {
    const result = await fetchProxy('PUT', 'update', 'site_settings_7523', data, id);
    return result.data || result;
  },
  createSiteSetting: async (data) => {
    const result = await fetchProxy('POST', 'create', 'site_settings_7523', data);
    return result.data || result;
  },

  getPageSections: async () => {
    return ncbService.getAllRecords('page_sections_7523');
  },
  updatePageSection: async (id, data) => {
    const result = await fetchProxy('PUT', 'update', 'page_sections_7523', data, id);
    return result.data || result;
  },
  createPageSection: async (data) => {
    const result = await fetchProxy('POST', 'create', 'page_sections_7523', data);
    return result.data || result;
  },

  getPageSectionItems: async () => {
    return ncbService.getAllRecords('page_section_items_7523');
  },
  updatePageSectionItem: async (id, data) => {
    const result = await fetchProxy('PUT', 'update', 'page_section_items_7523', data, id);
    return result.data || result;
  },
  createPageSectionItem: async (data) => {
    const result = await fetchProxy('POST', 'create', 'page_section_items_7523', data);
    return result.data || result;
  },
  deletePageSectionItem: async (id) => {
    const result = await fetchProxy('DELETE', 'delete', 'page_section_items_7523', null, id);
    return result.data || result;
  },

  getBusinessProfile: async () => {
    return ncbService.getAllRecords('business_profile_7523');
  },
  createBusinessProfile: async (data) => {
    const result = await fetchProxy('POST', 'create', 'business_profile_7523', data);
    return result.data || result;
  },
  updateBusinessProfile: async (id, data) => {
    const result = await fetchProxy('PUT', 'update', 'business_profile_7523', data, id);
    return result.data || result;
  },

  getRateSettings: async () => {
    return ncbService.getAllRecords('rates_config_7523');
  },
  updateRateSetting: async (id, data) => {
    const result = await fetchProxy('PUT', 'update', 'rates_config_7523', data, id);
    return result.data || result;
  }
};